"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye,
  EyeOff,
  Trash,
  Package,
  Tag,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import type { SectionItemOutput } from "@/interfaces/section-items";
import { numberToMoney } from "@/util/moneyConversion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface SectionItemCardProps {
  item: SectionItemOutput;
  position?: number;
  onToggleVisibility: (id: string) => void;
  onDelete: (id: string) => void;
  onMove: (itemId: string, direction: "up" | "down") => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

export function SectionItemCard({
  item,
  position,
  onToggleVisibility,
  onDelete,
  onMove,
  canMoveUp,
  canMoveDown,
}: SectionItemCardProps) {
  // Usar os dados já carregados via expand ao invés de fazer novas requests
  const isProduct = item.itemType === "PRODUCT";
  const details = isProduct ? item.product : item.offer;
  const productDetails = item.product;
  const offerDetails = item.offer;

  // Se os dados não estão disponíveis (não foi feito expand), exibir loading
  if (!details) {
    return (
      <Card className="p-3">
        <div className="flex items-center gap-3">
          <div className="w-20 h-20 rounded-xl bg-gray-200 animate-pulse" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded animate-pulse mb-2" />
            <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
          </div>
        </div>
      </Card>
    );
  }

  const name = details?.name || "Item não encontrado";

  // Calcular preços e desconto para ofertas
  const calculatePricing = () => {
    if (isProduct && productDetails) {
      return {
        currentPrice: productDetails.unitPrice || 0,
        originalPrice: null as number | null,
        discount: 0,
        quantity: 1,
      };
    } else if (!isProduct && offerDetails) {
      const totalOriginal =
        offerDetails.products?.reduce(
          (sum, p) => sum + (p.unitPrice || 0) * (p.quantity || 1),
          0
        ) || 0;
      const offerPrice = offerDetails.unitPrice || 0;
      const discount =
        totalOriginal > 0 && offerPrice < totalOriginal
          ? Math.round(((totalOriginal - offerPrice) / totalOriginal) * 100)
          : 0;
      const totalQuantity =
        offerDetails.products?.reduce((sum, p) => sum + (p.quantity || 1), 0) ||
        1;

      return {
        currentPrice: offerPrice,
        originalPrice: totalOriginal,
        discount,
        quantity: totalQuantity,
      };
    }

    // Fallback para dados incompletos
    return {
      currentPrice: 0,
      originalPrice: null as number | null,
      discount: 0,
      quantity: 1,
    };
  };

  const { currentPrice, originalPrice, discount, quantity } =
    calculatePricing();

  return (
    <TooltipProvider>
      <Card
        className={`group relative overflow-hidden border transition-all duration-200 ${
          !item.isVisible ? "opacity-60" : ""
        } hover:shadow-lg hover:border-[#EE7B11] hover:border-2 touch-manipulation`}
        style={{ borderRadius: "12px" }}
      >
        <CardContent style={{ padding: "16px", position: "relative" }}>
          {/* Botões de ação fixos no canto superior direito */}
          <div
            className="absolute top-2 right-2 flex items-center md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 bg-white/80 backdrop-blur-sm rounded-lg p-1 shadow-sm"
            style={{ gap: "4px", zIndex: 10 }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onMove(item.id, "up")}
                  disabled={!canMoveUp}
                  className="text-gray-500 hover:text-[#EE7B11] hover:bg-[#EE7B11]/10 disabled:opacity-30"
                  style={{
                    height: "28px",
                    width: "28px",
                    borderRadius: "8px",
                  }}
                >
                  <ArrowUp className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mover para cima</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onMove(item.id, "down")}
                  disabled={!canMoveDown}
                  className="text-gray-500 hover:text-[#EE7B11] hover:bg-[#EE7B11]/10 disabled:opacity-30"
                  style={{
                    height: "28px",
                    width: "28px",
                    borderRadius: "8px",
                  }}
                >
                  <ArrowDown className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mover para baixo</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onToggleVisibility(item.id)}
                  className={`${
                    item.isVisible
                      ? "text-green-500 hover:text-green-700 hover:bg-green-50"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                  style={{
                    height: "28px",
                    width: "28px",
                    borderRadius: "8px",
                  }}
                >
                  {item.isVisible ? (
                    <Eye className="h-3 w-3" />
                  ) : (
                    <EyeOff className="h-3 w-3" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {item.isVisible ? "Ocultar item" : "Mostrar item"}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(item.id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  style={{
                    height: "28px",
                    width: "28px",
                    borderRadius: "8px",
                  }}
                >
                  <Trash className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Deletar item</TooltipContent>
            </Tooltip>
          </div>

          {/* Header com Badge de Tipo */}
          <div
            className="flex items-center flex-wrap mb-3 pr-32"
            style={{ gap: "8px" }}
          >
            {/* Badge de Posição */}
            {position && (
              <Badge
                variant="outline"
                className="text-[#EE7B11] border-[#EE7B11]/30 bg-[#EE7B11]/5"
                style={{
                  fontSize: "11px",
                  padding: "2px 6px",
                  borderRadius: "8px",
                }}
              >
                #{position}
              </Badge>
            )}

            {/* Badge de Tipo */}
            <Badge
              className={`text-white font-medium ${
                isProduct
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "bg-[#EE7B11] hover:bg-[#d06a0a]"
              }`}
              style={{
                fontSize: "12px",
                padding: "4px 8px",
                borderRadius: "12px",
              }}
            >
              {isProduct ? (
                <>
                  <Package className="h-3 w-3 mr-1" />
                  Produto
                </>
              ) : (
                <>
                  <Tag className="h-3 w-3 mr-1" />
                  Oferta
                </>
              )}
            </Badge>

            {!item.isVisible && (
              <Badge
                variant="outline"
                className="text-gray-500"
                style={{
                  fontSize: "12px",
                  padding: "4px 8px",
                  borderRadius: "12px",
                }}
              >
                Oculto
              </Badge>
            )}
          </div>

          {/* Conteúdo Principal */}
          <div className="flex items-start" style={{ gap: "12px" }}>
            {/* Imagem 1:1 com altura fixa 80px */}
            <div
              className="relative overflow-hidden bg-gray-100 flex-shrink-0"
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "12px",
              }}
            >
              {isProduct && productDetails?.file ? (
                <img
                  src={
                    typeof productDetails.file === "string"
                      ? productDetails.file
                      : productDetails.file.url
                  }
                  alt={name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : !isProduct && offerDetails?.file ? (
                <img
                  src={
                    typeof offerDetails.file === "string"
                      ? offerDetails.file
                      : offerDetails.file.url
                  }
                  alt={name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div
                  className={`w-full h-full flex items-center justify-center ${
                    isProduct ? "text-blue-500" : "text-[#EE7B11]"
                  }`}
                >
                  {isProduct ? (
                    <Package className="h-8 w-8" />
                  ) : (
                    <Tag className="h-8 w-8" />
                  )}
                </div>
              )}
            </div>

            {/* Informações do Item */}
            <div className="flex-1 min-w-0">
              {/* Nome do Item */}
              <h3
                className="font-medium text-[#050505] line-clamp-2 leading-tight"
                style={{ marginBottom: "8px" }}
              >
                {name}
              </h3>

              {/* Informação de Quantidade (apenas para ofertas) */}
              {!isProduct && quantity > 1 && (
                <div style={{ marginBottom: "8px" }}>
                  <span
                    className="text-[#6B7280] font-medium"
                    style={{ fontSize: "12px" }}
                  >
                    Total: {quantity} itens
                  </span>
                </div>
              )}

              {/* Preços */}
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center flex-wrap"
                  style={{ gap: "8px" }}
                >
                  {/* Preço Original (se houver) */}
                  {originalPrice && originalPrice > currentPrice && (
                    <span
                      className="text-gray-500 line-through"
                      style={{ fontSize: "14px" }}
                    >
                      {numberToMoney(originalPrice)}
                    </span>
                  )}

                  {/* Preço Atual */}
                  <span
                    className="font-medium text-[#050505]"
                    style={{ fontSize: "14px" }}
                  >
                    {numberToMoney(currentPrice)}
                  </span>

                  {/* Badge de Desconto */}
                  {discount > 0 && (
                    <Badge
                      className="bg-green-500 text-white font-medium"
                      style={{
                        fontSize: "12px",
                        padding: "2px 8px",
                        borderRadius: "12px",
                      }}
                    >
                      -{discount}%
                    </Badge>
                  )}
                </div>
              </div>

              {/* Produtos da Oferta (se for oferta) */}
              {!isProduct &&
                offerDetails &&
                offerDetails.products &&
                offerDetails.products.length > 0 && (
                  <div className="border-t border-gray-100 mt-2 pt-2">
                    <div className="space-y-1">
                      {offerDetails.products.map((product, index) => (
                        <div
                          key={`${product.productId}-${index}`}
                          className="flex justify-between items-center"
                        >
                          <span
                            className="text-gray-600 text-xs truncate"
                            style={{ fontSize: "11px", fontWeight: "500" }}
                          >
                            {product.name}
                          </span>
                          <span
                            className="text-[#EE7B11] text-xs font-medium ml-2"
                            style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                          >
                            {product.quantity || 1}x
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
